import React from 'react';
import { PredictionResult } from '../types';
import ReactMarkdown from 'react-markdown';
import { ExternalLink, Navigation, Sparkles, Quote } from 'lucide-react';

interface AnalysisResultsProps {
  result: PredictionResult;
}

export const AnalysisResults: React.FC<AnalysisResultsProps> = ({ result }) => {
  return (
    <div className="space-y-8 animate-fade-in pb-10">
      {/* Text Analysis Card */}
      <div className="bg-white rounded-3xl shadow-xl shadow-stone-200/50 border border-stone-100 overflow-hidden">
        
        {/* Header Section */}
        <div className="bg-gradient-to-r from-amber-50/80 via-white to-white px-8 py-6 border-b border-stone-100 flex items-center gap-4">
          <div className="relative">
            <div className="absolute inset-0 bg-amber-400 blur-lg opacity-20 rounded-full"></div>
            <div className="relative bg-white p-2.5 rounded-2xl shadow-sm border border-amber-100 text-amber-600">
              <Sparkles className="w-6 h-6" />
            </div>
          </div>
          <div>
            <h2 className="text-xl font-extrabold text-stone-800 tracking-tight">Yapay Zeka Analiz Raporu</h2>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"></span>
              <p className="text-xs font-bold text-stone-400 uppercase tracking-wider">Arkeolojik Veri Değerlendirmesi</p>
            </div>
          </div>
        </div>

        {/* Content Section with Custom Typography */}
        <div className="p-8 md:p-10 bg-white">
          <ReactMarkdown
            components={{
              // Headings
              h1: ({node, ...props}) => (
                <h3 className="text-2xl font-black text-stone-900 mt-8 mb-4 pb-2 border-b border-stone-100" {...props} />
              ),
              h2: ({node, ...props}) => (
                <h4 className="text-lg font-bold text-stone-800 mt-8 mb-3 flex items-center gap-3" {...props}>
                  <span className="w-1 h-6 bg-amber-500 rounded-full"></span>
                  {props.children}
                </h4>
              ),
              h3: ({node, ...props}) => (
                <h5 className="text-base font-bold text-stone-700 mt-6 mb-2 uppercase tracking-wide" {...props} />
              ),
              // Paragraphs
              p: ({node, ...props}) => (
                <p className="text-[15px] text-stone-600 leading-8 mb-5 font-medium" {...props} />
              ),
              // Lists
              ul: ({node, ...props}) => (
                <ul className="my-6 space-y-3" {...props} />
              ),
              ol: ({node, ...props}) => (
                <ol className="my-6 space-y-3 list-decimal list-inside text-stone-700 font-bold marker:text-amber-600 marker:font-extrabold" {...props} />
              ),
              li: ({node, ...props}) => {
                // Determine if parent is OL or UL by checking props or context if needed, 
                // but ReactMarkdown structure makes direct parent access tricky in simple components.
                // We'll assume a generic style that works well for bullets (ul) since OL usually has numbers.
                // For a safer bet with mixed lists, we rely on parent UL styling for bullets.
                return (
                  <li className="pl-2 relative group text-stone-600 leading-7">
                    {/* Only show custom bullet if it's inside a UL (detected via CSS usually, but here we can't). 
                        Instead, we use standard list styles for simplicity but styled beautifully via container.
                    */}
                    <span className="group-hover:text-stone-900 transition-colors">{props.children}</span>
                  </li>
                )
              },
              // Emphasis
              strong: ({node, ...props}) => (
                <strong className="font-bold text-stone-900 bg-amber-50 px-1 rounded border border-amber-100/50" {...props} />
              ),
              // Blockquote
              blockquote: ({node, ...props}) => (
                <div className="my-6 relative pl-6 border-l-4 border-amber-300 bg-stone-50/50 py-4 pr-4 rounded-r-xl">
                  <Quote className="absolute top-2 left-2 w-4 h-4 text-amber-200 -z-10 opacity-50" />
                  <blockquote className="italic text-stone-600 font-medium" {...props} />
                </div>
              ),
              // Code (if any)
              code: ({node, ...props}) => (
                <code className="bg-stone-100 text-stone-600 px-1.5 py-0.5 rounded text-xs font-mono border border-stone-200" {...props} />
              )
            }}
            className="prose-none" // Disable default prose to use our custom components fully
          >
            {result.analysis}
          </ReactMarkdown>

          {/* Fallback styling for standard lists via wrapper class if custom components miss something */}
          <style>{`
            ul { list-style-type: none; padding-left: 0; }
            ul > li { position: relative; padding-left: 1.5rem; }
            ul > li::before { 
              content: ""; 
              position: absolute; 
              left: 0; 
              top: 0.6rem; 
              width: 6px; 
              height: 6px; 
              border-radius: 50%; 
              background-color: #f59e0b; /* amber-500 */
              transition: transform 0.2s;
            }
            ul > li:hover::before { transform: scale(1.5); }
          `}</style>
        </div>
      </div>

      {/* Grounding / Map Links */}
      {result.suggestedLocations.length > 0 && (
        <div className="relative bg-[#1c1917] text-stone-50 p-8 rounded-3xl shadow-2xl overflow-hidden group">
          {/* Background Effects */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-amber-600/20 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2 group-hover:bg-amber-600/30 transition-all duration-1000"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-rose-600/10 rounded-full blur-[80px] translate-y-1/2 -translate-x-1/2"></div>
          
          <h3 className="relative z-10 text-lg font-bold mb-6 flex items-center gap-3 text-amber-500">
            <div className="p-2 bg-amber-500/10 rounded-lg border border-amber-500/20">
               <Navigation className="w-5 h-5" />
            </div>
            <span>Google Haritalar Kaynaklı Öneriler</span>
          </h3>
          
          <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-4">
            {result.suggestedLocations.map((chunk, idx) => {
              if (chunk.maps) {
                return (
                  <a 
                    key={idx}
                    href={chunk.maps.uri}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex flex-col p-5 bg-white/5 hover:bg-white/10 rounded-2xl transition-all border border-white/5 hover:border-amber-500/30 group/card backdrop-blur-sm"
                  >
                    <div className="flex justify-between items-start mb-3">
                      <span className="font-bold text-lg text-stone-100 group-hover/card:text-amber-400 transition-colors">
                        {chunk.maps.title}
                      </span>
                      <ExternalLink className="w-4 h-4 text-stone-500 group-hover/card:text-white transition-colors opacity-50 group-hover/card:opacity-100" />
                    </div>
                    {chunk.maps.placeAnswerSources?.reviewSnippets?.[0] && (
                      <div className="relative pl-3 border-l-2 border-stone-700 group-hover/card:border-amber-500/50 transition-colors">
                         <p className="text-xs text-stone-400 italic leading-relaxed line-clamp-2">
                           "{chunk.maps.placeAnswerSources.reviewSnippets[0].text}"
                         </p>
                      </div>
                    )}
                  </a>
                );
              }
              return null;
            })}
          </div>
        </div>
      )}
    </div>
  );
};